import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import Button from "../components/Button";
import ConfirmDialog from "../components/common/ConfirmDialog";
import {
  deleteStripeAccount,
  fetchStripeAccounts,
  setStripeAccount,
  setStripeExpressAccount,
  setStripeExpressAccountV3,
} from "../shared/queries";
import { getOldToken } from "../shared/utils/tokenStore";
import Icon from "../components/Icon";
import StripeTableMockUp from "../components/StripeTableMockUp";
import Modal from "../components/Modal";
import ModalCard from "../components/ModalCard";
import ModalSection from "../components/ModalSection";
import ModalActions from "../components/ModalAction";
import DropdownMenu from "../components/DropdownMenu";

export default function StripeConnect() {
  const { t } = useTranslation();

  const { data } = useQuery("stripeAccounts", () =>
    fetchStripeAccounts(undefined)
  );
  const [currentAccount, setCurrentAccount] = useState<number | undefined>();
  const [showSetAll, setShowSetAll] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [viewStripe, setViewStripe] = useState(false);
  const [viewExpress, setViewExpress] = useState(false);
  const [commisionView, setCommissionView] = useState(false);

  const setAllMutation = useMutation(
    (id: string | number) =>
      setStripeAccount({
        action: "SETALL",
        stripe_account: id,
      }),
    {
      onSuccess: () => {
        setShowSetAll(false);
        toast.success(t("general.operationCompleted"));
      },
    }
  );
  const stipeExpressMutation = useMutation(() => setStripeExpressAccount(), {
    onSuccess: (data) => {
      window.open(data.results.url, "_self");
    },
  });

  useQuery(
    ["stipeExpressMutation", accountId],
    () => setStripeExpressAccountV3(accountId),
    {
      onSuccess: (data) => {
        if (data.results.url) {
          window.open(data.results.url, "_blank");
        }
      },
      enabled: accountId !== "",
    }
  );

  const deleteAccountMutation = useMutation(
    (id: string | number) => deleteStripeAccount(id),
    {
      onSuccess: () => {
        setShowDelete(false);
        toast.success(t("general.operationCompleted"));
      },
    }
  );

  return (
    <div>
      {/* STIPE EXPRESS */}

      <>
        <div className="flex flex-wrap gap-1">
          <Icon name="stripe-express" size="30px" />
          <p className="font-extrabold md:text-3xl text-xl">Stripe Express </p>
          <p className="font-bold md:text-3xl text-xl lowercase text-cyan-600">
            ({t("general.labels.recommendedLabel")})
          </p>
        </div>
        <p className={"text-gray-600 mt-8 md:text-md text-sm"}>
          {t("stripe.expressFirstHeader")}
        </p>
        <p className={"text-gray-600 mt-4 md:text-md text-sm"}>
          {t("stripe.expressFirstTitle")}
        </p>
        <p className={"text-gray-600 mt-4 md:text-md text-sm"}>
          {t("stripe.expressFirstDesc")}
        </p>
        <p className={"text-gray-600 mt-4 md:text-lg text-md font-bold"}>
          {t("stripe.expressTryIt")}
        </p>
        <ul className="mt-2">
          {t("stripe.expressTryItDescription")
            .split("///")
            .map((desc) => (
              <li className="flex md:text-md text-sm text-gray-600" key={desc}>
                <div>
                  <Icon
                    name="clipboard-check"
                    size="22px"
                    className="text-green-500"
                  />
                </div>
                <p>{desc}</p>
              </li>
            ))}
        </ul>

        <div className="flex justify-end mt-3 text-sm">
          <Button
            flat
            className="hover:underline"
            onClick={() => {
              window.open(
                "https://stripe.com/it/legal/connect-account",
                "_blank"
              );
            }}
          >
            {t("checkout.termsAndCondition")}
          </Button>
        </div>
        <div
          className={
            "text-gray-600 mt-4 flex gap-1 hover:underline cursor-pointer max-w-max"
          }
          onClick={() => setViewExpress((prev) => !prev)}
        >
          <Icon name="alert-attention" size="20px" />
          <p className="md:text-md text-sm">{t("stripe.expressExample")}</p>
        </div>
        {viewExpress && <StripeTableMockUp express />}

        <Button
          className={"mt-8 sm:ml-1 ml:0"}
          disabled={stipeExpressMutation.isLoading}
          loading={stipeExpressMutation.isLoading}
          onClick={() => {
            stipeExpressMutation.mutate();
          }}
          color={"primary"}
          label={t("stripe.activateExpress")}
        />

        <div className="my-8">
          <div className="flex items-center space-x-8 mb-6">
            <div className="font-semibold md:text-xl text-sm">
              Account Stripe Express
            </div>
            <div className="border-b flex-1" />
          </div>
          <div className="mt-2 space-y-1">
            {data &&
              data.results &&
              data.results
                .filter((account: any) => account.express)
                .map((account: any) => (
                  <div
                    className={
                      "flex flex-row items-center lg:space-x-2 justify-between border-b p-1"
                    }
                    key={account.id}
                  >
                    <div className="flex space-x-2">
                      <div className={"text-gray-600 md:text-sm text-xs"}>
                        {account.customer_id}
                      </div>

                      <div className={"font-medium md:text-sm text-xs"}>
                        {account.name}
                      </div>
                    </div>

                    <div
                      className={
                        "lg:space-x-2 lg:flex lg:flex-row text-sm hidden"
                      }
                    >
                      <Button
                        color={"info"}
                        className="bg-green-600 text-white"
                        flat
                        onClick={() => {
                          setAccountId(account.id);
                        }}
                        label={t("Express")}
                      />
                      <Button
                        color={"primary"}
                        flat
                        loading={setAllMutation.isLoading}
                        label={t("stripe.useForAll")}
                        onClick={() => {
                          setCurrentAccount(account.id);
                          setShowSetAll(true);
                        }}
                      />
                      <Button
                        color={"negative"}
                        flat
                        loading={deleteAccountMutation.isLoading}
                        onClick={() => {
                          setCurrentAccount(account.id);
                          setShowDelete(true);
                        }}
                        label={t("general.delete")}
                      />
                    </div>
                    <div className="relative ml-auto text-right overflow-visible lg:hidden">
                      <DropdownMenu
                        className="absolute right-0"
                        triggerLabel=""
                        // color="info"
                        options={[
                          {
                            label: "Express",
                            onClick: () => setAccountId(account.id),
                          },
                          {
                            label: t("stripe.useForAll"),
                            onClick: () => {
                              setCurrentAccount(account.id);
                              setShowSetAll(true);
                            },
                          },
                          {
                            label: t("general.delete"),
                            onClick: () => {
                              setCurrentAccount(account.id);
                              setShowDelete(true);
                            },
                          },
                        ]}
                      />
                    </div>
                  </div>
                ))}
          </div>
        </div>

        <div className="border-b-2 flex-1 mb-8 border-cyan-600" />
      </>

      {/* STIPE EXPRESS */}

      <img
        className={"md:h-10 h-8"}
        src="/images/logos/stripe.svg"
        alt="Stripe Connect"
      />

      <p className={"text-gray-600 mt-8 md:text-md text-sm"}>
        {t("stripe.ifEnable")}
      </p>
      <p className={"text-gray-600 mt-4 md:text-md text-sm"}>
        {t("stripe.ifNot")}
      </p>

      <p className={"text-gray-600 mt-4 flex gap-1 md:text-md text-sm"}>
        <div>
          <Icon name="alert-bell" size="20px" className="text-yellow-500" />
        </div>
        <p>{t("stripe.commission")}</p>
      </p>

      <div className="flex justify-end md:text-md text-sm mt-4">
        <Button
          flat
          className="hover:underline"
          onClick={() => {
            window.open(
              "https://stripe.com/it/legal/connect-account",
              "_blank"
            );
          }}
        >
          {t("checkout.termsAndCondition")}
        </Button>
      </div>

      <div className="flex justify-between flex-wrap  md:text-md text-sm mt-4">
        <div
          className={
            "text-gray-600 flex gap-1 hover:underline cursor-pointer max-w-max xl:order-1 order-2 lg:mt-0 mt-4"
          }
          onClick={() => setViewStripe((prev) => !prev)}
        >
          <Icon name="alert-attention" size="20px" />
          <p className="text-md">{t("stripe.expressExample")}</p>
        </div>
        <div
          className={
            "text-gray-600 flex gap-1 hover:underline cursor-pointer max-w-max xl:order-2 order-1"
          }
          onClick={() => {
            setCommissionView((prev) => !prev);
          }}
        >
          <p className="text-md">{t("stripe.commissionExample")}</p>
        </div>
      </div>

      {viewStripe && <StripeTableMockUp />}

      <Button
        className={"mt-8"}
        onClick={() => {
          window.open(
            "https://connect.stripe.com/oauth/authorize?scope=read_write&state=" +
              getOldToken() +
              "&response_type=code&client_id=ca_E4TfkUxhtE5y3yGeByWH3QoXineu2ixm",
            "_blank"
          );
        }}
        color={"primary"}
        label={t("stripe.activate")}
      />

      <div className="mt-8">
        <div className="flex items-center space-x-8 mb-6">
          <div className="font-semibold md:text-xl text-sm">Account Stripe</div>
          <div className="border-b flex-1" />
        </div>
        <div className="mt-2">
          {data &&
            data.results &&
            data.results
              .filter((account: any) => !account.express)
              .map((account: any) => (
                <div
                  className={
                    "flex flex-row items-center lg:space-x-2 justify-between border-b p-1"
                  }
                >
                  <div className="flex space-x-2">
                    <div className={"text-gray-600 md:text-sm text-xs"}>
                      {account.customer_id}
                    </div>

                    <div className={"font-medium md:text-sm text-xs"}>
                      {account.name}
                    </div>
                  </div>

                  <div
                    className={
                      "lg:space-x-2 lg:flex lg:flex-row md:text-sm text-xs hidden"
                    }
                  >
                    <Button
                      color={"primary"}
                      flat
                      loading={setAllMutation.isLoading}
                      label={t("stripe.useForAll")}
                      onClick={() => {
                        setCurrentAccount(account.id);
                        setShowSetAll(true);
                      }}
                    />
                    <Button
                      color={"negative"}
                      flat
                      loading={deleteAccountMutation.isLoading}
                      onClick={() => {
                        setCurrentAccount(account.id);
                        setShowDelete(true);
                      }}
                      label={t("general.delete")}
                    />
                  </div>
                  <div className="relative ml-auto text-right overflow-visible lg:hidden">
                    <DropdownMenu
                      className="absolute right-0"
                      triggerLabel=""
                      // color="info"
                      options={[
                        {
                          label: t("stripe.useForAll"),
                          onClick: () => {
                            setCurrentAccount(account.id);
                            setShowSetAll(true);
                          },
                        },
                        {
                          label: t("general.delete"),
                          onClick: () => {
                            setCurrentAccount(account.id);
                            setShowDelete(true);
                          },
                        },
                      ]}
                    />
                  </div>
                </div>
              ))}
        </div>
      </div>

      <ConfirmDialog
        askConfirmation={true}
        message={
          "Sei sicuro di voler associare questo account Stripe Connect a tutti gli appartamenti?"
        }
        confirmationKey={t("general.iConfirm")}
        visible={showSetAll}
        onCancel={() => {
          setShowSetAll(false);
        }}
        onConfirm={() => {
          if (currentAccount && data) setAllMutation.mutate(currentAccount);
        }}
      />

      <ConfirmDialog
        askConfirmation={true}
        message={"Sei sicuro di voler eliminare questo account Stripe Connect?"}
        confirmationKey={t("general.iConfirm")}
        visible={showDelete}
        onCancel={() => {
          setShowDelete(false);
        }}
        onConfirm={() => {
          if (currentAccount && data)
            deleteAccountMutation.mutate(currentAccount);
        }}
      />
      <Modal visible={commisionView} isResvDocs>
        <ModalCard
          className={"w-full max-w-xl"}
          title={t("general.commissions")}
        >
          <ModalSection className="p-5 md:text-sm text-xs">
            <div>
              <p className="font-semibold md:text-sm">
                {t("general.creditDebitCard")}
              </p>
              <p>
                {t("stripe.noCostActivation")}
                <p
                  className="hover:underline cursor-pointer text-cyan-700 max-w-max"
                  onClick={() =>
                    window.open(
                      "https://support.stripe.com/questions/what-s-the-difference-between-standard-and-premium-cards",
                      "_blank"
                    )
                  }
                >
                  {t("stripe.europeanStandardCard")}
                </p>
              </p>
              <div className="space-y-2 mt-3">
                <div className="border-b">
                  {t("stripe.descCommissionFirst")}
                </div>
                <div className="border-b">
                  {t("stripe.descCommissionSecond")}
                </div>
              </div>
            </div>
            <div className="w-full border-b-2 border-cyan-600 mt-3"></div>

            <div className="mb-4 mt-3">
              <p className="font-semibold">{t("general.internationalCards")}</p>
              <p>{t("stripe.intCostActivation")}</p>
              <div className="space-y-2 mt-3">
                <div className="border-b">
                  {t("stripe.descCommissionThird")}
                </div>
                <div className="border-b">
                  {t("stripe.descCommissionFourth")}
                </div>
              </div>
            </div>
          </ModalSection>
          <ModalActions onClose={() => setCommissionView(false)} />
        </ModalCard>
      </Modal>
    </div>
  );
}
